// The Mac app's side of a passkey ceremony: the page cannot use the relying
// party from its WebView, so it asks the shell, which shows the system's own
// passkey sheet. The shell says so with `data-passkeys="native"` on <html>,
// set with `data-shell="mac"` before any script of ours runs. The page posts
// `{passkey: {id, document, kind, options}}` on the shell's message handler (lib/shell.ts),
// `options` being the server's `publicKey` as it sent it, and the shell
// answers with a `poulpe-passkey` event on the window: `detail` is `{id,
// credential}`, the credential as `PublicKeyCredential.toJSON()` writes it, or
// `{id, error}`, with `cancelled`, `refused` or `unsupported`. Same options,
// same JSON forms as the browser's (passkeys.ts), so the store cannot tell.
import { hasShell, tellShell } from '../lib/shell'
import { browserPasskeys, PasskeyError, type Passkeys } from './passkeys'
import type { CreationOptions, RequestOptions } from './types'
import { uuid4 } from './uuid'

/** The event the shell answers with. */
const ANSWER = 'poulpe-passkey'

/** Whether the shell offers its passkeys. */
function native(): boolean {
  return typeof document !== 'undefined' && document.documentElement.dataset.passkeys === 'native' && hasShell()
}

/** The system's own passkeys, through the Mac app's shell. */
export const shellPasskeys: Passkeys = {
  supported: native,

  create(options: CreationOptions) {
    // Only the public key: the pages never ask for mediation (passkeys.ts).
    return ask('create', options.publicKey, isRegistration)
  },

  get(options: RequestOptions) {
    return ask('get', options.publicKey, isAuthentication)
  },
}

/** The shell's passkeys inside the Mac app that offers them, the browser's anywhere else. */
export function choosePasskeys(): Passkeys {
  return native() ? shellPasskeys : browserPasskeys
}

/** Asks the shell once and waits for the answer that carries this ask's id; any other is not ours. */
function ask<T>(
  kind: 'create' | 'get',
  options: PublicKeyCredentialCreationOptionsJSON | PublicKeyCredentialRequestOptionsJSON,
  valid: (credential: Record<string, unknown>) => boolean,
): Promise<T> {
  // No handler to post to: nobody would answer.
  if (!hasShell()) return Promise.reject(new PasskeyError('unsupported', 'There is no shell to ask.'))
  const document = globalThis.document.documentElement.dataset.sessionDocument
  if (!document) return Promise.reject(new PasskeyError('unsupported', 'There is no native document to ask from.'))
  return new Promise<T>((resolve, reject) => {
    const id = uuid4()
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<unknown>).detail
      if (!isObject(detail) || detail.id !== id) return
      window.removeEventListener(ANSWER, listener)
      window.clearTimeout(timeout)
      if (typeof detail.error === 'string') return reject(failure(detail.error))
      const credential = detail.credential
      if (isObject(credential) && valid(credential)) return resolve(credential as T)
      reject(new Error('The system gave a passkey that could not be read.'))
    }
    // A lost shell reply must not leave the page busy. Server ceremonies last five minutes.
    const timeout = window.setTimeout(() => {
      window.removeEventListener(ANSWER, listener)
      reject(new PasskeyError('cancelled', "The system's passkey sheet did not answer in time."))
    }, Math.min((options.timeout ?? 60_000) + 5_000, 300_000))
    window.addEventListener(ANSWER, listener)
    try {
      tellShell({ passkey: { id, document, kind, options } })
    } catch (err) {
      window.removeEventListener(ANSWER, listener)
      window.clearTimeout(timeout)
      reject(err)
    }
  })
}

/** What the shell's `error` means. The shell's `refused` is the sheet or the system saying no (an app not associated with the relying party, say): the same words as the server's refusal. */
function failure(error: string): Error {
  switch (error) {
    case 'cancelled':
    case 'refused':
    case 'unsupported':
      return new PasskeyError(error, `The system's passkey sheet said ${error}.`)
    default:
      return new Error(`The system's passkey sheet failed: ${error}.`)
  }
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function isText(value: unknown): value is string {
  return typeof value === 'string' && value !== ''
}

/** A credential's fields common to both kinds. */
function isCredential(credential: Record<string, unknown>): credential is Record<string, unknown> & { response: Record<string, unknown> } {
  return credential.type === 'public-key' && isText(credential.id) && isText(credential.rawId) && isObject(credential.response) && isText(credential.response.clientDataJSON)
}

function isRegistration(credential: Record<string, unknown>): boolean {
  if (!isCredential(credential)) return false
  const { attestationObject, transports } = credential.response
  return isText(attestationObject) && (transports === undefined || (Array.isArray(transports) && transports.every((t) => typeof t === 'string')))
}

function isAuthentication(credential: Record<string, unknown>): boolean {
  if (!isCredential(credential)) return false
  const { authenticatorData, signature, userHandle } = credential.response
  return isText(authenticatorData) && isText(signature) && (userHandle === undefined || userHandle === null || typeof userHandle === 'string')
}
